import React, {useEffect, useRef} from 'react';
import {
  AccessibilityInfo,
  ActivityIndicator,
  Dimensions,
  findNodeHandle,
  Image,
  InteractionManager,
  PixelRatio,
  View,
} from 'react-native';

import KeyboardFocusable from '/component/base/ExtKeyboard/KeyboardFocusable';
import Icon from '/component/base/Icon';
import Text from '/component/base/Text';
import {ManageMySiteTestIDs} from '/constant/TestIDs';
import {tt} from '/provider/TranslationProvider/TranslationProvider.util';

import {StaticFocusable} from '../../sections/StaticFocusable';

import {
  ACCEPTED_FORMATS,
  MAX_SIZE,
  UPLOAD_HINT,
} from './PhotosSection.constants';
import {styles} from './PhotosSection.style';
import type {PhotoCardProps} from './PhotosSection.types';

const {photosSection} = ManageMySiteTestIDs;

export const PhotoCard = ({
  type,
  slot,
  onPick,
  onConfirm,
  onCancel,
  onRetry,
}: PhotoCardProps) => {
  const label = String(
    type === 'before' ? tt('before', 'Before') : tt('after', 'After'),
  );

  const fontScale = PixelRatio.getFontScale();
  const screenWidth = Dimensions.get('window').width;
  const shouldStackButtons = fontScale >= 1.5 || screenWidth < 375;

  const slotName = `${type} photo`;
  const hasImage = Boolean(slot.uri?.trim?.());

  const displayUri =
    slot.status === 'uploaded' && slot.imageFile?.uri
      ? slot.imageFile.uri
      : slot.uri;

  const showEmptyState = slot.status === 'empty' && !hasImage;
  const showImageState =
    slot.status === 'preview' ||
    slot.status === 'uploading' ||
    slot.status === 'uploaded' ||
    slot.status === 'error';

  const isUploading = slot.status === 'uploading';

  const confirmButtonRef = useRef<any>(null);
  const retryButtonRef = useRef<any>(null);
  const uploadButtonRef = useRef<any>(null);
  const replaceButtonRef = useRef<any>(null);
  const cancelButtonRef = useRef<any>(null);

  const prevStatusRef = useRef(slot.status);

  const handlePickPress = () => {
    onPick();
  };

  useEffect(() => {
    const prevStatus = prevStatusRef.current;
    const currentStatus = slot.status;

    prevStatusRef.current = currentStatus;

    if (prevStatus === 'preview' && currentStatus === 'uploading') {
      return;
    }

    if (prevStatus === 'empty' && currentStatus === 'preview') {
      let timeoutId: ReturnType<typeof setTimeout>;

      const task = InteractionManager.runAfterInteractions(() => {
        timeoutId = setTimeout(() => {
          const node = findNodeHandle(confirmButtonRef?.current);

          if (node) {
            AccessibilityInfo.setAccessibilityFocus(node);
          }
        }, 300);
      });

      return () => {
        task.cancel?.();

        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    }

    if (prevStatus === 'uploading' && currentStatus === 'uploaded') {
      let timeoutId: ReturnType<typeof setTimeout>;

      const task = InteractionManager.runAfterInteractions(() => {
        timeoutId = setTimeout(() => {
          const node = findNodeHandle(replaceButtonRef?.current);

          if (node) {
            AccessibilityInfo.setAccessibilityFocus(node);
          }
        }, 300);
      });

      return () => {
        task.cancel?.();

        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    }

    if (
      (prevStatus === 'preview' || prevStatus === 'error') &&
      currentStatus === 'empty'
    ) {
      let timeoutId: ReturnType<typeof setTimeout>;

      const task = InteractionManager.runAfterInteractions(() => {
        timeoutId = setTimeout(() => {
          const node = findNodeHandle(uploadButtonRef?.current);

          if (node) {
            AccessibilityInfo.setAccessibilityFocus(node);
          }
        }, 300);
      });

      return () => {
        task.cancel?.();

        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    }

    if (prevStatus === 'uploaded' && currentStatus === 'preview') {
      let timeoutId: ReturnType<typeof setTimeout>;

      const task = InteractionManager.runAfterInteractions(() => {
        timeoutId = setTimeout(() => {
          const node = findNodeHandle(confirmButtonRef?.current);

          if (node) {
            AccessibilityInfo.setAccessibilityFocus(node);
          }
        }, 300);
      });

      return () => {
        task.cancel?.();

        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    }

    if (prevStatus === 'preview' && currentStatus === 'uploaded') {
      let timeoutId: ReturnType<typeof setTimeout>;

      const task = InteractionManager.runAfterInteractions(() => {
        timeoutId = setTimeout(() => {
          const node = findNodeHandle(replaceButtonRef?.current);

          if (node) {
            AccessibilityInfo.setAccessibilityFocus(node);
          }
        }, 100);
      });

      return () => {
        task.cancel?.();

        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    }
  }, [slot.status]);

  useEffect(() => {
    if (slot.status !== 'error') {
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout>;

    const task = InteractionManager.runAfterInteractions(() => {
      timeoutId = setTimeout(() => {
        const node = findNodeHandle(retryButtonRef?.current);

        if (node) {
          AccessibilityInfo.setAccessibilityFocus(node);
        }
      }, 300);
    });

    return () => {
      task.cancel?.();

      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [slot.status]);

  return (
    <View style={styles.photoCard} accessible={false}>
      <StaticFocusable
        role="header"
        label={label}
        testID={`transformation photo-label-${label}`}>
        <Text
          allowFontScaling
          maxFontSizeMultiplier={2}
          style={styles.label}
          testID={`transformation photo-label-${label}`}
          accessible={false}>
          {label}
        </Text>
      </StaticFocusable>

      <View style={styles.imageWrapper} accessible={false}>
        {showEmptyState && (
          <KeyboardFocusable
            viewRef={uploadButtonRef}
            style={styles.uploadBox}
            onPress={handlePickPress}
            accessible
            accessibilityRole="button"
            accessibilityLabel={`Tap to upload ${slotName}. ${ACCEPTED_FORMATS}. ${MAX_SIZE}`}
            accessibilityHint={UPLOAD_HINT}
            testID={photosSection.uploadButton(type)}>
            <Icon
              name="arrow-up-circle"
              style={styles.uploadIcon}
              testID={photosSection.uploadIcon(type)}
              accessible={false}
              importantForAccessibility="no-hide-descendants"
            />

            <Text
              allowFontScaling
              maxFontSizeMultiplier={1.5}
              style={styles.uploadText}
              testID={photosSection.uploadText(type)}
              accessible={false}>
              {tt('upload', 'Tap to upload')}
            </Text>

            <Text
              allowFontScaling
              maxFontSizeMultiplier={1.5}
              style={styles.uploadSubText}
              testID={photosSection.uploadSubtext(type)}
              accessible={false}>
              {ACCEPTED_FORMATS}
            </Text>

            <Text
              allowFontScaling
              maxFontSizeMultiplier={1.5}
              style={styles.uploadTextSize}
              testID={photosSection.uploadSizeText(type)}
              accessible={false}>
              {MAX_SIZE}
            </Text>
          </KeyboardFocusable>
        )}

        {showImageState && hasImage && (
          <KeyboardFocusable
            viewRef={replaceButtonRef}
            accessible
            accessibilityRole="imagebutton"
            accessibilityLabel={`${label} transformation photo`}
            accessibilityHint="Double tap to change photo"
            onPress={handlePickPress}
            testID={photosSection.photoImage(type)}
            disabled={isUploading}
            accessibilityState={{disabled: isUploading}}>
            <Image
              source={{uri: displayUri, cache: 'reload'}}
              style={[styles.photoImage, isUploading && styles.disabled]}
              resizeMode="cover"
              accessible={false}
              importantForAccessibility="no-hide-descendants"
            />
            {isUploading && (
              <View
                style={styles.loaderOverlay}
                testID={photosSection.uploadLoader(type)}
                accessible
                accessibilityRole="progressbar"
                accessibilityLabel={`Uploading ${slotName}`}>
                <ActivityIndicator color="#111827" />
              </View>
            )}
          </KeyboardFocusable>
        )}
      </View>

      {(slot.status === 'preview' || isUploading) && (
        <View
          style={[
            styles.actionRow,
            shouldStackButtons && styles.actionRowStacked,
          ]}
          testID={photosSection.actionRow(type)}
          accessible={false}>
          <View
            style={[
              shouldStackButtons || isUploading
                ? styles.actionBtnWrapperFull
                : styles.actionBtnWrapper,
            ]}
            accessible={false}>
            <KeyboardFocusable
              viewRef={confirmButtonRef}
              style={[styles.actionBtn, styles.confirmBtn]}
              onPress={onConfirm}
              disabled={isUploading}
              accessible
              accessibilityRole="button"
              accessibilityState={{disabled: isUploading}}
              accessibilityLabel={isUploading ? 'Uploading...' : 'Confirm'}
              accessibilityHint={
                isUploading
                  ? `Please wait while ${slotName} is uploading`
                  : `Confirms the selected ${slotName}`
              }
              testID={photosSection.confirmButton(type)}>
              <Text
                style={styles.confirmBtnText}
                accessible={false}
                allowFontScaling
                maxFontSizeMultiplier={1.5}
                numberOfLines={1}>
                {isUploading
                  ? tt('uploading', 'Uploading...')
                  : tt('confirm', 'Confirm')}
              </Text>
            </KeyboardFocusable>
          </View>

          {!isUploading && !shouldStackButtons && (
            <View style={styles.actionSpacerHorizontal} accessible={false} />
          )}

          {!isUploading && shouldStackButtons && (
            <View style={styles.actionSpacerVertical} accessible={false} />
          )}

          {!isUploading && (
            <View
              style={[
                shouldStackButtons
                  ? styles.actionBtnWrapperFull
                  : styles.actionBtnWrapper,
              ]}
              accessible={false}>
              <KeyboardFocusable
                viewRef={cancelButtonRef}
                style={[styles.actionBtn, styles.cancelBtn]}
                onPress={onCancel}
                accessible
                accessibilityRole="button"
                accessibilityLabel={`Cancel ${slotName} selection`}
                accessibilityHint={`Cancels the selected ${slotName}`}
                testID={photosSection.cancelButton(type)}>
                <Text
                  style={styles.cancelBtnText}
                  accessible={false}
                  allowFontScaling
                  maxFontSizeMultiplier={1.5}
                  numberOfLines={1}>
                  {tt('cancel', 'Cancel')}
                </Text>
              </KeyboardFocusable>
            </View>
          )}
        </View>
      )}

      {slot.status === 'uploaded' && hasImage && (
        <KeyboardFocusable
          viewRef={replaceButtonRef}
          style={styles.replaceBtn}
          onPress={handlePickPress}
          accessible
          accessibilityRole="button"
          accessibilityLabel={`Replace ${slotName}`}
          accessibilityHint={`Replaces the ${slotName}`}
          testID={photosSection.replaceButton(type)}>
          <Text
            allowFontScaling
            maxFontSizeMultiplier={1.5}
            style={styles.replaceBtnText}
            testID={photosSection.replaceText(type)}
            accessible={false}>
            {tt('replace', 'Replace')}
          </Text>
        </KeyboardFocusable>
      )}

      {slot.status === 'error' && (
        <View style={styles.retryContainer} accessible={false}>
          <StaticFocusable
            role="alert"
            label="Upload failed. Try again."
            style={styles.retryTextContainer}>
            <Text
              style={styles.errorText}
              accessible={false}
              allowFontScaling
              maxFontSizeMultiplier={1.2}>
              {tt('uploadError', 'Upload failed. Try again.')}
            </Text>
          </StaticFocusable>

          <View
            style={[
              styles.retryActionRow,
              shouldStackButtons && styles.retryActionRowStacked,
            ]}
            accessible={false}>
            <KeyboardFocusable
              viewRef={retryButtonRef}
              onPress={onRetry}
              style={[
                styles.retryBtn,
                shouldStackButtons && styles.fullWidthBtn,
              ]}
              accessible
              accessibilityRole="button"
              accessibilityLabel={`Retry ${slotName} upload`}
              accessibilityHint={`Attempts to upload the ${slotName} again`}
              testID={photosSection.retryButton(type)}>
              <Text
                style={styles.retryBtnText}
                accessible={false}
                allowFontScaling
                maxFontSizeMultiplier={1.5}>
                {tt('retry', 'Retry Upload')}
              </Text>
            </KeyboardFocusable>

            <KeyboardFocusable
              viewRef={cancelButtonRef}
              onPress={onCancel}
              style={[
                styles.retryCancelButton,
                shouldStackButtons && styles.fullWidthBtn,
              ]}
              accessible
              accessibilityRole="button"
              accessibilityLabel={`Cancel ${slotName} upload`}
              accessibilityHint={`Cancels the failed ${slotName} upload`}
              testID={photosSection.retryCancelButton(type)}>
              <Text
                style={styles.retryCancelText}
                accessible={false}
                allowFontScaling
                maxFontSizeMultiplier={1.5}>
                {tt('cancel', 'Cancel')}
              </Text>
            </KeyboardFocusable>
          </View>
        </View>
      )}
    </View>
  );
};

export default PhotoCard;
